import { useMemo } from 'react';
import {
  ArrowLeft,
  Save,
  HelpCircle,
} from 'lucide-react';
import { useHistory, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  useEnquiryQuery,
  useCreateEnquiryMutation,
  useUpdateEnquiryMutation,
} from '@features/enquiries/hooks/useEnquiriesQuery.js';
import { POLICY_TYPE_LABELS } from '@repo/constants';
import type { PolicyType } from '@repo/types';
import PageLoader from '@components/ui/PageLoader.js';
import Button from '@components/ui/Button.js';
import Input from '@components/ui/Input.js';
import Select from '@components/ui/Select.js';
import AppShellPage from '@components/layout/AppShellPage.js';

const enquirySchema = z.object({
  name: z.string().min(1, 'Name is required'),
  mobileNumber: z.string().refine((v) => /^\d{10}$/.test(v.replace(/\D/g, '')), {
    message: 'Enter a valid 10-digit mobile number',
  }),
  policyType: z.string().min(1, 'Policy type is required'),
  referredBy: z.string().optional(),
  remindOn: z.string().optional(),
  status: z.string().optional(),
});

type FormValues = z.infer<typeof enquirySchema>;

const ALL_TYPE_OPTIONS = Object.entries(POLICY_TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06 } },
};

function EnquiryFormBody({
  defaultValues,
  isEdit,
  id,
}: {
  defaultValues: FormValues;
  isEdit: boolean;
  id: string | undefined;
}) {
  const history = useHistory();
  const createMutation = useCreateEnquiryMutation();
  const updateMutation = useUpdateEnquiryMutation();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(enquirySchema),
    defaultValues,
  });

  const onSubmit = async (values: FormValues) => {
    try {
      const payload = {
        name: values.name,
        mobileNumber: values.mobileNumber,
        policyType: values.policyType as PolicyType,
        referredBy: values.referredBy ?? null,
        remindOn: values.remindOn ?? null,
      };

      if (isEdit && id) {
        await updateMutation.mutateAsync({ id, data: payload });
        toast.success('Enquiry updated successfully');
      } else {
        await createMutation.mutateAsync(payload);
        toast.success('Enquiry created successfully');
      }
      history.push('/enquiries');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'An error occurred. Please try again.');
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <>
      <motion.div
        variants={stagger}
        initial="hidden"
        animate="show"
        className="max-w-2xl mx-auto space-y-6"
      >
        <form
          onSubmit={(e) => {
            void handleSubmit(onSubmit)(e);
          }}
          className="space-y-5 bg-surface border border-line p-6 rounded-2xl shadow-sm text-left"
        >
          <Input
            label="Client Name"
            placeholder="Enter client full name"
            required
            error={errors.name?.message}
            {...register('name')}
          />

          <Input
            label="Mobile Number"
            placeholder="e.g. 9876543210"
            required
            error={errors.mobileNumber?.message}
            {...register('mobileNumber')}
          />

          <Controller
            name="policyType"
            control={control}
            render={({ field }) => (
              <Select
                label="Interested Policy Type"
                value={field.value}
                onValueChange={field.onChange}
                options={ALL_TYPE_OPTIONS}
                error={errors.policyType?.message}
              />
            )}
          />

          <Input
            label="Referred By"
            placeholder="e.g. Friend, Google Search, Walk-in"
            error={errors.referredBy?.message}
            {...register('referredBy')}
          />

          <Input
            label="Remind On"
            type="date"
            error={errors.remindOn?.message}
            {...register('remindOn')}
          />

          <div className="hidden md:flex gap-3 pt-3 border-t border-line">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => {
                history.goBack();
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              className="flex-1 flex items-center justify-center gap-1.5 whitespace-nowrap"
              loading={isSaving}
              onClick={() => {
                void handleSubmit(onSubmit)();
              }}
            >
              <Save size={15} />
              <span>{isEdit ? 'Update' : 'Save'}</span>
            </Button>
          </div>
        </form>
      </motion.div>

      {/* ── Sticky bottom save bar — mobile only ── */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-30 border-t border-line bg-surface/95 backdrop-blur-xl px-4 py-3 pb-safe">
        <div className="flex gap-2.5 max-w-6xl mx-auto">
          <Button
            variant="outline"
            size="lg"
            className="flex-1"
            onClick={() => {
              history.goBack();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="lg"
            loading={isSaving}
            leftIcon={<Save size={15} />}
            className="flex-1 whitespace-nowrap"
            onClick={() => {
              void handleSubmit(onSubmit)();
            }}
          >
            {isEdit ? 'Update' : 'Save'}
          </Button>
        </div>
      </div>
    </>
  );
}

export default function EnquiryFormPage() {
  const { id } = useParams<{ id?: string }>();
  const history = useHistory();
  const isEdit = !!id;

  const { data, isLoading } = useEnquiryQuery(id ?? '');
  const existing = data?.data;

  const defaultValues = useMemo(() => {
    if (existing) {
      return {
        name: existing.name,
        mobileNumber: existing.mobileNumber,
        policyType: existing.policyType,
        referredBy: existing.referredBy ?? '',
        remindOn: existing.remindOn ? existing.remindOn.slice(0, 10) : '',
        status: existing.status,
      };
    }
    return {
      name: '',
      mobileNumber: '',
      policyType: 'MOTOR',
      referredBy: '',
      remindOn: '',
      status: 'OPEN',
    };
  }, [existing]);

  if (isLoading && isEdit) return <PageLoader variant="default" />;

  return (
    <AppShellPage
      icon={HelpCircle}
      title={isEdit ? 'Edit Client Enquiry' : 'New Client Enquiry'}
      subtitle={isEdit ? 'Modify enquiry information and update triage status.' : 'Log a new client enquiry and specify triage reminders.'}
      narrow
      breadcrumb={[
        { label: 'Enquiries', onClick: () => { history.push('/enquiries'); } },
        { label: isEdit ? 'Edit' : 'New' },
      ]}
      actions={
        <Button
          variant="outline"
          size="sm"
          className="flex items-center gap-1.5"
          onClick={() => {
            history.push('/enquiries');
          }}
        >
          <ArrowLeft size={14} />
          <span>Back</span>
        </Button>
      }
    >
      <EnquiryFormBody
        key={existing?.id ?? 'create'}
        defaultValues={defaultValues}
        isEdit={isEdit}
        id={id}
      />
    </AppShellPage>
  );
}
